import type { DialogConf } from '@Primitive/ExDialog';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { ToolbarIconInfo } from '@Primitive/ToolbarIconButton';
import { PlanogramEditorCellTypes } from './Shapes';
// -------------------------------------------------------------------------------------------------
export const enum PortKeys {
    Data = 'data',
    Save = 'save',
    ToolbarButtonClicked = 'toolbarbuttonclicked'
}
// -------------------------------------------------------------------------------------------------
// widget settings
export interface PlanogramEditorData {
    editable: boolean;
    toolbarButtons: ToolbarIconInfo[];
}
// -------------------------------------------------------------------------------------------------
export interface PlanogramEditorState {
    dialogInfo: DialogConf;
    openStencil: boolean;
    groupsVisibility: { [key: string]: boolean };
    searchValueProducts: string;
    searchValueDecoratives: string;
    comment: string;
    hideUsedStencilProducts: boolean;
    hideUsedStencilDecoratives: boolean;
}
// -------------------------------------------------------------------------------------------------
// input payload
export interface IPlanogramEditorDataPort {
    title: string;
    comment: string;
    products: { [key: string]: PlanogramEditorProduct };
    plan: PlanogramEditorPlan;
    cells: PlanogramEditorCell[];
    changes: PlanogramEditorChange[];
    template: boolean;
    decoratives: { [key: number]: PlanogramEditorDecorative };
}
// -------------------------------------------------------------------------------------------------
// output payload
export interface IPlanogramEditorSavePort {
    comment: string;
    cells: PlanogramEditorCell[];
}
// -------------------------------------------------------------------------------------------------
export interface PlanogramEditorProduct {
    name: string;
    code: string; // sku
    category: string; // space group
    image: string;
    width: number; // cm
    height: number; // cm
}
// -------------------------------------------------------------------------------------------------
export interface PlanogramEditorDecorative {
    name: string;
    code: string; // auto-increment
    image: string;
}
// -------------------------------------------------------------------------------------------------
export interface PlanogramEditorCellSettings {
    depth: number;
    rows: number;
    columns: number;
    comment: string;
}
// -------------------------------------------------------------------------------------------------
export interface PlanogramEditorPlan {
    image: string;
    width: number; // cm
    height: number; // cm
}
// -------------------------------------------------------------------------------------------------
// graph element
export interface PlanogramEditorCell {
    type: PlanogramEditorCellTypes;
    id: string; // random
    code: string; // ref to product or decorative
    settings: PlanogramEditorCellSettings;
    positionX: number;
    positionY: number;
    canvasWidth: number;
    canvasHeight: number;
}
// -------------------------------------------------------------------------------------------------
export interface PlanogramEditorChange {
    id: string; // ref to cell
    type: PlanogramEditorChangeType; // flags
    newCode: string;
    oldCode: string;
    newDepth: number;
    oldDepth: number;
    newRows: number;
    oldRows: number;
    newColumns: number;
    oldColumns: number;
}
// -------------------------------------------------------------------------------------------------
export enum PlanogramEditorChangeType {
    None = 0,
    PlanogramAdded = 1,
    PlanogramDeleted = 2,
    SkuAdded = 4,
    SkuDeleted = 8,
    SkuReplaced = 16,
    DepthChanged = 32,
    PositionChanged = 64,
    FacesChanged = 128
}
// -------------------------------------------------------------------------------------------------
export const planogramEditorDataPortValidator = () => {
    return JsonSchemaValidator.GetInstance('planogram_editor_data_port_validator', {
        type: 'object',
        properties: {
            title: { type: 'string' },
            comment: { type: 'string' },
            template: { type: 'boolean' },
            products: {
                type: 'object',
                patternProperties: {
                    '^.*$': {
                        name: { type: 'string' },
                        code: { type: 'string' },
                        category: { type: 'string' },
                        image: { type: 'string' },
                        width: { type: 'number' },
                        height: { type: 'number' }
                    }
                }
            },
            plan: {
                type: 'object',
                properties: {
                    image: { type: 'string' },
                    width: { type: 'number' },
                    height: { type: 'number' },
                    canvasWidth: { type: 'number' },
                    canvasHeight: { type: 'number' }
                }
            },
            cells: {
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        id: { type: 'string' },
                        code: { type: 'string' },
                        settings: {
                            type: 'object',
                            properties: {
                                depth: { type: 'number' },
                                rows: { type: 'number' },
                                columns: { type: 'number' }
                            }
                        },
                        positionX: { type: 'number' },
                        positionY: { type: 'number' },
                        canvasWidth: { type: 'number' },
                        canvasHeight: { type: 'number' }
                    }
                }
            },
            changes: {
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        id: { type: 'string' },
                        newCode: { type: 'string' },
                        oldCode: { type: 'string' },
                        newDepth: { type: 'number' },
                        oldDepth: { type: 'number' },
                        newRows: { type: 'number' },
                        oldRows: { type: 'number' },
                        newColumns: { type: 'number' },
                        oldColumns: { type: 'number' }
                    }
                }
            },
            decoratives: {
                type: 'object',
                patternProperties: {
                    '^.*$': {
                        name: { type: 'string' },
                        code: { type: 'string' },
                        image: { type: 'string' }
                    }
                }
            }
        }
    });
};
// -------------------------------------------------------------------------------------------------